import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { LanguageProvider, useLanguage } from './hooks/use-language';
import { Header } from './components/layout/header';
import { Footer } from './components/layout/footer';
import { LoadingOverlay } from './components/layout/loading-overlay';
import { MobileTabNavigation } from './components/layout/MobileTabNavigation';
import { AdminAuthProvider } from './hooks/useAdminAuth';
import { CrmAuthProvider } from './hooks/useCrmAuth';
import { SEOProvider, useSEOContext } from './hooks/use-seo';
import { PageHelmet } from './components/layout/PageHelmet';
import { AdminProtectedRoute } from './components/admin/AdminProtectedRoute';
import { CrmProtectedRoute } from './components/crm/CrmProtectedRoute';

// Home stays eager — it's the LCP-critical route and must render without
// waiting for an additional chunk. Everything else is lazy-loaded so that
// public visitors don't pay the cost of admin / CRM / Useful-article code.
import { Home } from './pages/Home';

// Helper: many of these pages export named components rather than defaults,
// so we adapt them for React.lazy().
const lazyNamed = <T extends Record<string, any>>(
  loader: () => Promise<T>,
  name: keyof T,
) => lazy(() => loader().then((m) => ({ default: m[name] as React.ComponentType<any> })));

// CRM
const CrmLoginPage = lazyNamed(() => import('./pages/crm/CrmLoginPage'), 'CrmLoginPage');
const CrmLayout = lazyNamed(() => import('./pages/crm/CrmLayout'), 'CrmLayout');

// Public pages (excluding Home)
const Catalog = lazyNamed(() => import('./pages/Catalog'), 'Catalog');
const Useful = lazyNamed(() => import('./pages/Useful'), 'Useful');
const UsefulArticlePage = lazyNamed(() => import('./pages/UsefulArticle'), 'UsefulArticlePage');
const UsefulArticleUnderlay = lazyNamed(() => import('./pages/UsefulArticleUnderlay'), 'UsefulArticleUnderlay');
const UsefulArticleWarmFloor = lazyNamed(() => import('./pages/UsefulArticleWarmFloor'), 'UsefulArticleWarmFloor');
const UsefulArticleWearClass = lazyNamed(() => import('./pages/UsefulArticleWearClass'), 'UsefulArticleWearClass');
const UsefulArticleSPCvsLaminate = lazyNamed(() => import('./pages/UsefulArticleSPCvsLaminate'), 'UsefulArticleSPCvsLaminate');
const UsefulArticleSPCPanelsBuyWall = lazyNamed(() => import('./pages/UsefulArticleSPCPanelsBuyWall'), 'UsefulArticleSPCPanelsBuyWall');
const UsefulArticleSPCWallPanels = lazyNamed(() => import('./pages/UsefulArticleSPCWallPanels'), 'UsefulArticleSPCWallPanels');
const UsefulArticleSPCBathroomKitchen = lazyNamed(() => import('./pages/UsefulArticleSPCBathroomKitchen'), 'UsefulArticleSPCBathroomKitchen');
const UsefulArticleSPCSizesJoints = lazyNamed(() => import('./pages/UsefulArticleSPCSizesJoints'), 'UsefulArticleSPCSizesJoints');
const UsefulArticleSPCvsPVH = lazyNamed(() => import('./pages/UsefulArticleSPCvsPVH'), 'UsefulArticleSPCvsPVH');
const UsefulArticleCarpetTileIntro = lazyNamed(() => import('./pages/UsefulArticleCarpetTileIntro'), 'UsefulArticleCarpetTileIntro');
const UsefulArticleCarpetTileBuy = lazyNamed(() => import('./pages/UsefulArticleCarpetTileBuy'), 'UsefulArticleCarpetTileBuy');
const UsefulArticleCarpetTileCommercial = lazyNamed(() => import('./pages/UsefulArticleCarpetTileCommercial'), 'UsefulArticleCarpetTileCommercial');
const UsefulArticleCarpetTileUhod = lazyNamed(() => import('./pages/UsefulArticleCarpetTileUhod'), 'UsefulArticleCarpetTileUhod');
const UsefulArticleSPCScratches = lazyNamed(() => import('./pages/UsefulArticleSPCScratches'), 'UsefulArticleSPCScratches');
const UsefulArticleSPCPanelsCleaning = lazyNamed(() => import('./pages/UsefulArticleSPCPanelsCleaning'), 'UsefulArticleSPCPanelsCleaning');
const UsefulArticleSPCProsConsReviews = lazyNamed(() => import('./pages/UsefulArticleSPCProsConsReviews'), 'UsefulArticleSPCProsConsReviews');
const UsefulArticleSPCLaminateBuy = lazyNamed(() => import('./pages/UsefulArticleSPCLaminateBuy'), 'UsefulArticleSPCLaminateBuy');
const UsefulArticleSPCIntro = lazyNamed(() => import('./pages/UsefulArticleSPCIntro'), 'UsefulArticleSPCIntro');
const Portfolio = lazyNamed(() => import('./pages/Portfolio'), 'Portfolio');
const News = lazyNamed(() => import('./pages/News'), 'News');
const About = lazyNamed(() => import('./pages/About'), 'About');
const ESG = lazyNamed(() => import('./pages/ESG'), 'ESG');
const WhereToBuy = lazyNamed(() => import('./pages/WhereToBuy'), 'WhereToBuy');
const BecomeDealer = lazyNamed(() => import('./pages/BecomeDealer'), 'BecomeDealer');
const ProductPage = lazyNamed(() => import('./pages/ProductPage'), 'ProductPage');

// Admin pages — fully lazy: never bundled with the public homepage
const AdminLayout = lazyNamed(() => import('./pages/admin/AdminLayout'), 'AdminLayout');
const AdminLoginPage = lazyNamed(() => import('./pages/admin/LoginPage'), 'AdminLoginPage');
const AdminDashboardPage = lazyNamed(() => import('./pages/admin/Dashboard'), 'AdminDashboardPage');
const ApplicationsPage = lazyNamed(() => import('./pages/admin/ApplicationsPage'), 'ApplicationsPage');
const DealersPage = lazyNamed(() => import('./pages/admin/DealersPage'), 'DealersPage');
const PagesAdmin = lazyNamed(() => import('./pages/admin/PagesAdmin'), 'PagesAdmin');
const CatalogAdmin = lazyNamed(() => import('./pages/admin/CatalogAdmin'), 'CatalogAdmin');
const PortfolioAdmin = lazyNamed(() => import('./pages/admin/PortfolioAdmin'), 'PortfolioAdmin');
const NewsAdmin = lazyNamed(() => import('./pages/admin/NewsAdmin'), 'NewsAdmin');
const WhereToBuyAdmin = lazyNamed(() => import('./pages/admin/WhereToBuyAdmin'), 'WhereToBuyAdmin');
const CrmAdminPage = lazyNamed(() => import('./pages/admin/CrmAdminPage'), 'CrmAdminPage');
const AnalyticsAdmin = lazyNamed(() => import('./pages/admin/AnalyticsAdmin'), 'AnalyticsAdmin');
const HomeAdmin = lazyNamed(() => import('./pages/admin/HomeAdmin'), 'HomeAdmin');
const FactoryAdmin = lazyNamed(() => import('./pages/admin/FactoryAdmin'), 'FactoryAdmin');
const ESGAdmin = lazyNamed(() => import('./pages/admin/ESGAdmin'), 'ESGAdmin');
const BecomeDealerAdmin = lazyNamed(() => import('./pages/admin/BecomeDealerAdmin'), 'BecomeDealerAdmin');
const FooterAdmin = lazyNamed(() => import('./pages/admin/FooterAdmin'), 'FooterAdmin');
const SPCCalculatorAdmin = lazyNamed(() => import('./pages/admin/SPCCalculatorAdmin'), 'SPCCalculatorAdmin');
const SPCPanelCalculatorAdmin = lazyNamed(() => import('./pages/admin/SPCPanelCalculatorAdmin'), 'SPCPanelCalculatorAdmin');
const CarpetTileCalculatorAdmin = lazyNamed(() => import('./pages/admin/CarpetTileCalculatorAdmin'), 'CarpetTileCalculatorAdmin');

import { Toaster } from 'react-hot-toast';
import { cn } from './lib/utils';
import { bootstrapAnalytics, trackPageView } from './lib/analytics';
import { prefetchAllData } from './lib/globalDataCache';
import {
  PAGE_SLUG_MAP,
  resolveHubIdFromSlug,
  resolveArticleIdFromSlug,
  resolveIncomingUsefulPath,
  getUsefulRootSlug,
  type Lang
} from './lib/localizedRoutes';

// ─── Supported languages ──────────────────────────────────────────────────────

const VALID_LANGS = ['ru', 'en', 'uz'] as const;
type ValidLang = (typeof VALID_LANGS)[number];
const DEFAULT_LANG: ValidLang = 'ru';

function isValidLang(v: string | undefined): v is ValidLang {
  return VALID_LANGS.includes(v as ValidLang);
}

// ─── Redirect helpers ─────────────────────────────────────────────────────────

/** Redirect legacy paths (without lang prefix) → /ru/... */
function LegacyRedirect() {
  const location = useLocation();
  return <Navigate to={`/${DEFAULT_LANG}${location.pathname}${location.search}${location.hash}`} replace />;
}

/** Guard: if :lang is invalid, redirect to /ru keeping rest of path */
function LangGuard({ children }: { children: React.ReactNode }) {
  const { lang } = useParams<{ lang: string }>();
  const location = useLocation();

  if (!isValidLang(lang)) {
    // Strip the invalid lang segment and redirect to /ru/...
    const rest = location.pathname.replace(/^\/[^/]+/, '');
    return <Navigate to={`/${DEFAULT_LANG}${rest}${location.search}${location.hash}`} replace />;
  }

  return <>{children}</>;
}

// ─── Redirect to correct localized slug for a page key ───────────────────────

/**
 * Used inside PublicLayout to redirect old English slugs → correct localized slug.
 * e.g. /ru/about → /ru/o-zavode, /uz/become-dealer → /uz/diler-bolish
 */
function LocalizedPageRedirect({ pageKey }: { pageKey: string }) {
  const { lang } = useParams<{ lang: string }>();
  const resolvedLang: ValidLang = isValidLang(lang) ? lang : DEFAULT_LANG;
  const localizedSlug = PAGE_SLUG_MAP[pageKey]?.[resolvedLang] ?? pageKey;
  return <Navigate to={`/${resolvedLang}/${localizedSlug}`} replace />;
}

// ─── Useful Dispatcher — handles all useful routes and redirects ──────────────

function UsefulDispatcher() {
  const { lang, hub: hubSlug, article: articleSlug } = useParams<{ lang: string; hub?: string; article?: string }>();
  const location = useLocation();
  const resolvedLang = (isValidLang(lang) ? lang : DEFAULT_LANG) as Lang;

  // 1. Handle Redirects (e.g. /ru/useful -> /ru/poleznoe, or wrong hub/article slugs for the lang)
  if (hubSlug) {
    const redirectPath = resolveIncomingUsefulPath(resolvedLang, hubSlug, articleSlug);
    if (redirectPath) return <Navigate to={redirectPath} replace />;
  } else {
    // Check root redirect (e.g. /ru/useful -> /ru/poleznoe)
    const rootSlug = getUsefulRootSlug(resolvedLang);
    const currentRoot = location.pathname.split('/')[2];
    if (currentRoot !== rootSlug) {
      return <Navigate to={`/${resolvedLang}/${rootSlug}`} replace />;
    }
  }

  // 2. Dispatch to specific component or fallback to data-driven UsefulArticlePage
  if (articleSlug) {
    const hubId = resolveHubIdFromSlug(hubSlug!, resolvedLang) || hubSlug;
    const articleId = resolveArticleIdFromSlug(hubSlug!, articleSlug, resolvedLang) || articleSlug;

    // Specific article components mapping (canonical IDs)
    if (hubId === 'spc-laminat') {
      if (articleId === 'podlozhka') return <UsefulArticleUnderlay />;
      if (articleId === 'teplyy-pol-vannaya') return <UsefulArticleWarmFloor />;
      if (articleId === 'klass-tolshina-zashchitnyy-sloy') return <UsefulArticleWearClass />;
      if (articleId === 'spc-vs-laminat') return <UsefulArticleSPCvsLaminate />;
      if (articleId === 'spc-caryapiny-pyatna') return <UsefulArticleSPCScratches />;
      if (articleId === 'plyusy-minusy-otzyvy') return <UsefulArticleSPCProsConsReviews />;
      if (articleId === 'kupit-cena') return <UsefulArticleSPCLaminateBuy />;
      if (articleId === 'chto-takoe-spc') return <UsefulArticleSPCIntro />;
    }
    if (hubId === 'spc-paneli') {
      if (articleId === 'kupit-cena') return <UsefulArticleSPCPanelsBuyWall />;
      if (articleId === 'chto-eto') return <UsefulArticleSPCWallPanels />;
      if (articleId === 'vannaya-kuhnya') return <UsefulArticleSPCBathroomKitchen />;
      if (articleId === 'razmery-styki-ugly') return <UsefulArticleSPCSizesJoints />;
      if (articleId === 'spc-vs-pvh') return <UsefulArticleSPCvsPVH />;
      if (articleId === 'paneli-chistka') return <UsefulArticleSPCPanelsCleaning />;
    }
    if (hubId === 'carpet-tile') {
      if (articleId === 'chto-eto') return <UsefulArticleCarpetTileIntro />;
      if (articleId === 'kupit-cena-office') return <UsefulArticleCarpetTileBuy />;
      if (articleId === 'kommercheskaya-iznos') return <UsefulArticleCarpetTileCommercial />;
      if (articleId === 'uhod-chistka') return <UsefulArticleCarpetTileUhod />;
    }

    // Fallback for articles defined only in useful-data.ts (data-driven rendering)
    return <UsefulArticlePage />;
  }

  // It's a Hub or the Root Knowledge Base
  return <Useful />;
}

// ─── Public Layout ────────────────────────────────────────────────────────────

// Single Helmet renderer — one per public route, no accumulation
function PublicSEOManager() {
  return <PageHelmet />;
}

function PublicLayout() {
  const location = useLocation();
  const { lang } = useParams<{ lang: string }>();
  const langPrefix = `/${lang || DEFAULT_LANG}`;

  // Use localized slugs for about-page detection
  const resolvedLang: ValidLang = isValidLang(lang) ? lang : DEFAULT_LANG;
  const aboutSlug = PAGE_SLUG_MAP['about']?.[resolvedLang] ?? 'about';
  const isAboutPage = location.pathname === `${langPrefix}/${aboutSlug}`;

  const usefulSlug = PAGE_SLUG_MAP['useful']?.[resolvedLang] ?? 'useful';
  const newsSlug = PAGE_SLUG_MAP['news']?.[resolvedLang] ?? 'news';
  const portfolioSlug = PAGE_SLUG_MAP['portfolio']?.[resolvedLang] ?? 'portfolio';
  const esgSlug = PAGE_SLUG_MAP['esg']?.[resolvedLang] ?? 'esg';
  const whereToBuySlug = PAGE_SLUG_MAP['where-to-buy']?.[resolvedLang] ?? 'where-to-buy';
  const becomeDealerSlug = PAGE_SLUG_MAP['become-dealer']?.[resolvedLang] ?? 'become-dealer';

  const mainPages = [
    langPrefix,
    `${langPrefix}/catalog`,
    `${langPrefix}/${usefulSlug}`,
    `${langPrefix}/${portfolioSlug}`,
    `${langPrefix}/${newsSlug}`,
    `${langPrefix}/${aboutSlug}`,
    `${langPrefix}/${esgSlug}`,
    `${langPrefix}/${whereToBuySlug}`,
    `${langPrefix}/${becomeDealerSlug}`,
  ];
  // Catalog category pages (/catalog/<category>) count as main pages too —
  // the swipe-tab navigation stays enabled across them so the user can keep
  // sliding to other top-level sections after coming back from a product.
  const KNOWN_CATALOG_CATEGORIES = ['spc-laminate', 'spc-panels', 'carpet-tile', 'accessories'];
  const isCatalogCategory =
    location.pathname.startsWith(`${langPrefix}/catalog/`) &&
    KNOWN_CATALOG_CATEGORIES.includes(location.pathname.split('/')[3] ?? '') &&
    location.pathname.split('/').filter(Boolean).length === 3;
  const isMainPage = mainPages.some(page => location.pathname === page) || isCatalogCategory;

  const isUsefulSection = location.pathname.startsWith(`${langPrefix}/${usefulSlug}`);
  // We keep this class check if needed for CSS, though dispatcher handles routing
  const isSPCLaminatHub = location.pathname.includes('spc-laminat') || location.pathname.includes('spc-laminate');

  // Resolve the "logical" page key for the current pathname so we can detect
  // language switches (where only the lang prefix or the localized slug changes
  // but the user is still on the same page) and avoid scrolling them to the top.
  const logicalPathRef = React.useRef<string | null>(null);
  const currentLogicalPath = React.useMemo(() => {
    const segs = location.pathname.split('/').filter(Boolean);
    // Drop the lang segment, then map the localized slug to its canonical pageKey.
    const langless = isValidLang(segs[0]) ? segs.slice(1) : segs;
    if (langless.length === 0) return 'home';
    const firstSlug = langless[0];
    // Try to resolve the localized slug to a canonical page key (e.g. ru `o-zavode` → `about`).
    const langForResolve: Lang = (isValidLang(segs[0]) ? segs[0] : DEFAULT_LANG) as Lang;
    const pageKey =
      Object.keys(PAGE_SLUG_MAP).find(
        (k) => PAGE_SLUG_MAP[k]?.[langForResolve] === firstSlug,
      ) ?? firstSlug;
    return [pageKey, ...langless.slice(1)].join('/');
  }, [location.pathname]);

  React.useEffect(() => {
    const prev = logicalPathRef.current;
    logicalPathRef.current = currentLogicalPath;
    // First render — no jump.
    if (prev === null) return;
    // Same logical page (e.g. user just switched language) — preserve scroll.
    if (prev === currentLogicalPath) return;
    // Real navigation: original behaviour (desktop or non-main page).
    if (window.innerWidth >= 1024 || !isMainPage) {
      window.scrollTo(0, 0);
    }
  }, [currentLogicalPath, isMainPage]);

  // Prefetch all public page data on first render — eliminates loading states on navigation.
  // Pass the current URL language so critical requests target the visible language first;
  // other languages are deferred to idle inside prefetchAllData().
  React.useEffect(() => {
    const initialLang = isValidLang(lang) ? lang : DEFAULT_LANG;
    prefetchAllData(initialLang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bootstrap analytics on initial load and every route change
  React.useEffect(() => {
    bootstrapAnalytics(location.pathname);
  }, [location.pathname]);

  // Keep session alive: update lastSeenAt every 5 minutes while page is open
  React.useEffect(() => {
    const t = setInterval(() => {
      trackPageView(location.pathname);
    }, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [location.pathname]);

  return (
    <div className="flex flex-col min-h-screen selection:bg-primary selection:text-white bg-background">
      {/* Single Helmet renderer — one per public route, no accumulation */}
      <PublicSEOManager />
      <Header />
      <MobileTabNavigation>
        <main className={cn(
          'flex-1 pb-[env(safe-area-inset-bottom)]',
          isUsefulSection && 'useful-section-mobile-fix',
          isSPCLaminatHub && 'spc-laminat-hub-active',
        )}>
          <Suspense fallback={<LoadingOverlay />}>
            <Routes>
              <Route index element={<Home />} />
              <Route path="catalog" element={<Catalog />} />
              <Route path="catalog/:category" element={<Catalog />} />
              <Route path="catalog/:category/:slug" element={<ProductPage />} />

              {/* ── Unified Useful section (Root, Hub, Article) with localized slugs ── */}
              {/* These routes all use the Dispatcher which handles localization and redirects */}
              <Route path="useful" element={<UsefulDispatcher />} />
              <Route path="useful/:hub" element={<UsefulDispatcher />} />
              <Route path="useful/:hub/:article" element={<UsefulDispatcher />} />

              <Route path="poleznoe" element={<UsefulDispatcher />} />
              <Route path="poleznoe/:hub" element={<UsefulDispatcher />} />
              <Route path="poleznoe/:hub/:article" element={<UsefulDispatcher />} />

              <Route path="foydali" element={<UsefulDispatcher />} />
              <Route path="foydali/:hub" element={<UsefulDispatcher />} />
              <Route path="foydali/:hub/:article" element={<UsefulDispatcher />} />

              {/* Compatibility redirects for old sub-paths if any */}
              <Route path="useful/uhod" element={<Navigate to={`${langPrefix}/${usefulSlug}`} replace />} />
              <Route path="useful/uhod/*" element={<Navigate to={`${langPrefix}/${usefulSlug}`} replace />} />

              {/* ── Standalone pages — localized slugs ─────────────────────── */}

              {/* about: ru=o-zavode, en=about, uz=zavod-haqida */}
              <Route path="o-zavode" element={<About />} />
              <Route path="zavod-haqida" element={<About />} />
              {/* en 'about' is also the legacy English slug — keep it working for all langs */}
              <Route path="about" element={<About />} />

              {/* news: ru=novosti, en=news, uz=yangiliklar */}
              <Route path="novosti" element={<News />} />
              <Route path="novosti/:category" element={<News />} />
              <Route path="yangiliklar" element={<News />} />
              <Route path="yangiliklar/:category" element={<News />} />
              {/* en 'news' legacy */}
              <Route path="news" element={<News />} />
              <Route path="news/:category" element={<News />} />

              {/* portfolio: same slug for all langs */}
              <Route path="portfolio" element={<Portfolio />} />

              {/* esg: same slug for all langs */}
              <Route path="esg" element={<ESG />} />

              {/* where-to-buy: ru=gde-kupit, en=where-to-buy, uz=qayerdan-sotib-olish */}
              <Route path="gde-kupit" element={<WhereToBuy />} />
              <Route path="qayerdan-sotib-olish" element={<WhereToBuy />} />
              {/* en 'where-to-buy' legacy */}
              <Route path="where-to-buy" element={<WhereToBuy />} />

              {/* become-dealer: ru=stat-dilerom, en=become-dealer, uz=diler-bolish */}
              <Route path="stat-dilerom" element={<BecomeDealer />} />
              <Route path="diler-bolish" element={<BecomeDealer />} />
              {/* en 'become-dealer' legacy */}
              <Route path="become-dealer" element={<BecomeDealer />} />
            </Routes>
          </Suspense>
        </main>
      </MobileTabNavigation>
      <div className={cn(isMainPage ? 'hidden lg:block' : '')}>
        <Footer className={isAboutPage ? 'pt-10 lg:pt-12' : ''} />
      </div>
    </div>
  );
}

// ─── App Routes ───────────────────────────────────────────────────────────────

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingOverlay />}>
      <Routes>
        {/* CRM Routes */}
        <Route path="/crm/login" element={<CrmLoginPage />} />
        <Route
          path="/crm"
          element={
            <CrmProtectedRoute>
              <CrmLayout />
            </CrmProtectedRoute>
          }
        />

        {/*
       * /admin/login — публичная (без guard), но если уже залогинен — редирект на /admin
       * (логика внутри самой LoginPage через useAdminAuth)
       */}
        <Route path="/admin/login" element={<AdminLoginPage />} />

        {/*
       * Все /admin/* оборачиваем в AdminProtectedRoute.
       * AdminLayout — только shell (сайдбар + outlet), без guard-логики.
       * Guard находится в AdminProtectedRoute — чисто и надёжно.
       */}
        <Route
          path="/admin"
          element={
            <AdminProtectedRoute>
              <AdminLayout />
            </AdminProtectedRoute>
          }
        >
          <Route index element={<AdminDashboardPage />} />
          <Route path="pages" element={<PagesAdmin />} />
          <Route path="catalog" element={<CatalogAdmin />} />
          <Route path="portfolio" element={<PortfolioAdmin />} />
          <Route path="news" element={<NewsAdmin />} />
          <Route path="factory" element={<FactoryAdmin />} />
          <Route path="esg" element={<ESGAdmin />} />
          <Route path="where-to-buy" element={<WhereToBuyAdmin />} />
          <Route path="crm" element={<CrmAdminPage />} />
          <Route path="analytics" element={<AnalyticsAdmin />} />
          <Route path="home" element={<HomeAdmin />} />
          <Route path="become-dealer" element={<BecomeDealerAdmin />} />
          <Route path="footer" element={<FooterAdmin />} />
          <Route path="spc-calculator" element={<SPCCalculatorAdmin />} />
          <Route path="spc-panel-calculator" element={<SPCPanelCalculatorAdmin />} />
          <Route path="carpet-tile-calculator" element={<CarpetTileCalculatorAdmin />} />
          {/* Обратная совместимость */}
          <Route path="dealers" element={<DealersPage />} />
          <Route path="applications" element={<ApplicationsPage />} />
        </Route>

        {/* Неизвестные /admin/* → login */}
        <Route path="/admin/*" element={<Navigate to="/admin/login" replace />} />

        {/* ─── Root redirect: / → /ru ─────────────────────────────────────────── */}
        <Route path="/" element={<Navigate to={`/${DEFAULT_LANG}`} replace />} />

        {/* ─── Legacy redirects: bare paths without lang prefix → /ru/... ────── */}
        {/* catalog and useful keep their English slugs as canonical — redirect to /ru/... */}
        <Route path="/catalog" element={<LegacyRedirect />} />
        <Route path="/catalog/*" element={<LegacyRedirect />} />
        <Route path="/useful" element={<LegacyRedirect />} />
        <Route path="/useful/*" element={<LegacyRedirect />} />

        {/* Standalone pages: bare English slug → /ru/<localized> */}
        <Route path="/about" element={<Navigate to={`/${DEFAULT_LANG}/${PAGE_SLUG_MAP['about'][DEFAULT_LANG]}`} replace />} />
        <Route path="/news" element={<Navigate to={`/${DEFAULT_LANG}/${PAGE_SLUG_MAP['news'][DEFAULT_LANG]}`} replace />} />
        <Route path="/news/*" element={<Navigate to={`/${DEFAULT_LANG}/${PAGE_SLUG_MAP['news'][DEFAULT_LANG]}`} replace />} />
        <Route path="/portfolio" element={<Navigate to={`/${DEFAULT_LANG}/${PAGE_SLUG_MAP['portfolio'][DEFAULT_LANG]}`} replace />} />
        <Route path="/esg" element={<Navigate to={`/${DEFAULT_LANG}/${PAGE_SLUG_MAP['esg'][DEFAULT_LANG]}`} replace />} />
        <Route path="/where-to-buy" element={<Navigate to={`/${DEFAULT_LANG}/${PAGE_SLUG_MAP['where-to-buy'][DEFAULT_LANG]}`} replace />} />
        <Route path="/become-dealer" element={<Navigate to={`/${DEFAULT_LANG}/${PAGE_SLUG_MAP['become-dealer'][DEFAULT_LANG]}`} replace />} />

        {/* ─── Public site with lang prefix: /:lang/* ─────────────────────────── */}
        <Route
          path="/:lang/*"
          element={
            <LangGuard>
              <PublicLayout />
            </LangGuard>
          }
        />
      </Routes>
    </Suspense>
  );
}

// ─── Root App ─────────────────────────────────────────────────────────────────

function App() {
  return (
    <HelmetProvider>
      <AdminAuthProvider>
        <CrmAuthProvider>
          <LanguageProvider>
            {/* SEOProvider wraps the entire app so both PublicLayout and pages share the same context */}
            <SEOProvider>
              <Toaster position="top-center" reverseOrder={false} />
              <LoadingOverlay />
              <Router>
                <AppRoutes />
              </Router>
            </SEOProvider>
          </LanguageProvider>
        </CrmAuthProvider>
      </AdminAuthProvider>
    </HelmetProvider>
  );
}

export default App;
